import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientService } from '../../services/clientService';

// Async thunks
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (_, { rejectWithValue }) => {
    try {
      const clients = await clientService.getAll();
      return clients;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la încărcarea clienților');
    }
  }
);

export const fetchClientById = createAsyncThunk(
  'clients/fetchClientById',
  async (id, { rejectWithValue }) => {
    try {
      const client = await clientService.getById(id);
      return client;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Client nu a fost găsit');
    }
  }
);

export const createClient = createAsyncThunk(
  'clients/createClient',
  async (clientData, { rejectWithValue }) => {
    try {
      const client = await clientService.create(clientData);
      return client;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la crearea clientului');
    }
  }
);

export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async ({ id, clientData }, { rejectWithValue }) => {
    try {
      const client = await clientService.update(id, clientData);
      return client;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la actualizarea clientului');
    }
  }
);

export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (id, { rejectWithValue }) => {
    try {
      await clientService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la ștergerea clientului');
    }
  }
);

export const searchClients = createAsyncThunk(
  'clients/searchClients',
  async (searchTerm, { rejectWithValue }) => {
    try {
      const clients = await clientService.search(searchTerm);
      return clients;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la căutarea clienților');
    }
  }
);

export const importClients = createAsyncThunk(
  'clients/importClients',
  async (file, { rejectWithValue }) => {
    try {
      const result = await clientService.importFromExcel(file);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la importul clienților');
    }
  }
);

const initialState = {
  clients: [],
  selectedClient: null,
  loading: false,
  error: null,
  searchResults: [],
  searchLoading: false,
  importing: false,
  importResult: null,
};

const clientSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedClient: (state) => {
      state.selectedClient = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearImportResult: (state) => {
      state.importResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch client by ID
      .addCase(fetchClientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedClient = action.payload;
      })
      .addCase(fetchClientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create client
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients.push(action.payload);
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update client
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.clients.findIndex(client => client.id === action.payload.id);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
        if (state.selectedClient?.id === action.payload.id) {
          state.selectedClient = action.payload;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete client
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = state.clients.filter(client => client.id !== action.payload);
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search clients
      .addCase(searchClients.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchClients.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchClients.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      })
      // Import clients
      .addCase(importClients.pending, (state) => {
        state.importing = true;
        state.error = null;
      })
      .addCase(importClients.fulfilled, (state, action) => {
        state.importing = false;
        state.importResult = action.payload;
        // Add imported clients to the list
        if (action.payload.clients) {
          state.clients.push(...action.payload.clients);
        }
      })
      .addCase(importClients.rejected, (state, action) => {
        state.importing = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSelectedClient, clearSearchResults, clearImportResult } = clientSlice.actions;
export default clientSlice.reducer;